/**
 * @file launchPlans.ts
 * @brief Pure form model for Super Admin → Billing → Plans & seats: the four
 *        launch plans (Free, Pro, Business, Enterprise) from GET /api/admin/plans
 *
 * @details
 * DES 22-10-2026: launch plans are exactly Free, Pro, Business, Enterprise;
 * GB pays in GBP, everyone else in USD (BACKEND B3 c906e6f). Each setting
 * arrives with a plain label, a `kind` and `effective` = what billing reads
 * now (the code default when no row is stored). The form shows `effective`
 * in plain units and Save sends only the keys whose stored value would
 * change, the same rule as the 21-10-2026 plan prices fix.
 *
 * Display units: money in pounds/dollars ("14.99"), extra Intelligence rates
 * may be a fraction of a penny ("0.012"); Notes fair use in hours; Ask limits
 * "-1" = Unlimited (a toggle on the page, never a typed -1).
 *
 * B20 / W17 (24-10-2026): Free can be monthly or one-time with an expiry
 * (`choice` FREE_PLAN_RESET_MODE + FREE_PLAN_EXPIRY_DAYS) and every plan
 * number is a setting: per-plan history days, Notes minutes and fair use,
 * interpretation minimums, Business maximum seats, the voice-note length
 * (`seconds`). A 0 or -1 that means "unlimited / never / shared" reads as
 * words, never as a bare 0. In one-time mode the Free numbers are a total
 * ("300 min, once"), so the change lines read the mode before and after.
 *
 * Related: components/SubscriptionPlansCard.tsx, BACKEND
 * src/features/admin/planCards.mjs, planSettings.mjs, priceSettingsGuard.mjs
 *
 * @author DES (prepared with Claude)
 * @date 22-10-2026
 */

export const PLAN_CURRENCIES = ["GBP", "USD"] as const;
export type PlanCurrency = (typeof PLAN_CURRENCIES)[number];

export const CURRENCY_SYMBOL: Record<PlanCurrency, string> = {
  GBP: "£",
  USD: "$",
};

export type PlanSettingKind =
  | "minor"
  | "minutes"
  | "count"
  | "limit"
  | "days"
  | "seats"
  | "switch"
  // B20: a fixed list (`options`), e.g. FREE_PLAN_RESET_MODE.
  | "choice"
  // B20: the voice-note length.
  | "seconds"
  // A kind this build does not know: shown read-only, never sent.
  | "other";

/** When a change applies and who it reaches (BACKEND B20 planSettingRules.mjs). */
export interface PlanRule {
  appliesFrom: string;
  affects: string;
}

export interface PlanSetting {
  key: string;
  label: string;
  kind: PlanSettingKind;
  currency?: PlanCurrency;
  interval?: "month" | "year";
  /** Stored row, or null when there is no row yet (code default applies). */
  value: string | null;
  /** What billing reads now. B20: a string for a choice. */
  effective: number | boolean | string | null;
  /** B20: the allowed values of a choice. */
  options?: string[];
  /** B20: when a change applies and who it reaches. */
  rule?: PlanRule | null;
}

export type FormValues = Record<string, string>;

/** Notes fair use is stored in minutes and shown in hours (B20: per plan too). */
export const HOURS_KEYS = new Set([
  "NOTES_FAIR_USE_MINUTES",
  "PLAN_PRO_NOTES_FAIR_USE_MINUTES",
  "PLAN_BUSINESS_NOTES_FAIR_USE_MINUTES",
]);

export const FREE_RESET_MODE_KEY = "FREE_PLAN_RESET_MODE";
export const FREE_EXPIRY_KEY = "FREE_PLAN_EXPIRY_DAYS";
/** The Free numbers that are "a month" or "in total" by the reset mode. */
export const FREE_MODE_NUMBER_KEYS = ["FREE_PLAN_NOTES_MINUTES", "FREE_PLAN_ASK_LIMIT"] as const;

export type FreeResetMode = "monthly" | "one_time";

/** "monthly" unless the value says one-time (an old backend has no mode). */
export function freeModeOf(display: string | null | undefined): FreeResetMode {
  return (display ?? "").trim() === "one_time" ? "one_time" : "monthly";
}

/** The segmented control's words for a choice value. */
export const CHOICE_LABELS: Record<string, string> = {
  monthly: "Every month",
  one_time: "One time",
};

/**
 * Smallest value per key where it differs from the kind's rule (BACKEND
 * planSettings.mjs PLAN_NUMBER_RULES, B20): 0 / -1 carry a meaning there.
 */
const MIN_BY_KEY: Record<string, number> = {
  FREE_PLAN_HISTORY_DAYS: 0,
  FREE_PLAN_EXPIRY_DAYS: 0,
  PLAN_PRO_HISTORY_DAYS: 0,
  PLAN_BUSINESS_HISTORY_DAYS: 0,
  PLAN_PRO_NOTES_FAIR_USE_MINUTES: 0,
  PLAN_BUSINESS_NOTES_FAIR_USE_MINUTES: 0,
  PLAN_BUSINESS_MAX_SEATS: 0,
  note_max_duration_seconds: 30,
};

/** What a 0 (or -1 on a limit) means for a key; null = it is just a number. */
export function zeroMeaning(key: string): string | null {
  if (key === "FREE_PLAN_HISTORY_DAYS") return "All";
  if (/^PLAN_(PRO|BUSINESS)_HISTORY_DAYS$/.test(key)) return "Unlimited";
  if (key === FREE_EXPIRY_KEY) return "Never";
  if (/^PLAN_(PRO|BUSINESS)_NOTES_FAIR_USE_MINUTES$/.test(key)) return "Shared fair use";
  if (key === "PLAN_BUSINESS_MAX_SEATS") return "No maximum";
  return null;
}

/** Plain labels for the B20 settings (the server's carry "(0 = …)" hints). */
const PLAIN_LABELS: Record<string, string> = {
  FREE_PLAN_RESET_MODE: "Free minutes",
  FREE_PLAN_EXPIRY_DAYS: "Expires",
  FREE_PLAN_HISTORY_DAYS: "Days of history",
  FREE_PLAN_NOTES_MINUTES: "Meeting Notes minutes",
  FREE_PLAN_ASK_LIMIT: "Ask AI questions",
  PLAN_PRO_HISTORY_DAYS: "Days of history",
  PLAN_BUSINESS_HISTORY_DAYS: "Days of history",
  PLAN_PRO_NOTES_FAIR_USE_MINUTES: "Meeting Notes fair use, hours a month",
  PLAN_BUSINESS_NOTES_FAIR_USE_MINUTES: "Meeting Notes fair use per person, hours a month",
  PLAN_PRO_NOTES_MINUTES: "Meeting Notes minutes a month",
  PLAN_BUSINESS_NOTES_MINUTES: "Meeting Notes minutes per person a month",
  PLAN_BUSINESS_MAX_SEATS: "Maximum seats",
  note_max_duration_seconds: "Voice note: longest recording",
};

/** The short line under a B20 field in the Edit sheet: what 0 / -1 means. */
export function fieldHint(s: Pick<PlanSetting, "key">): string | null {
  const k = s.key;
  if (k === FREE_EXPIRY_KEY) return "Days from sign-up. 0 = never. One-time Free only.";
  if (k === "FREE_PLAN_HISTORY_DAYS") return "0 = all history.";
  if (/^PLAN_(PRO|BUSINESS)_HISTORY_DAYS$/.test(k)) return "0 = unlimited. Older items are hidden, never deleted.";
  if (/^PLAN_(PRO|BUSINESS)_NOTES_FAIR_USE_MINUTES$/.test(k)) return "0 = the shared fair use on All plans.";
  if (/^PLAN_(PRO|BUSINESS)_NOTES_MINUTES$/.test(k)) return "Unlimited = only the fair use applies.";
  if (k === "PLAN_BUSINESS_MAX_SEATS") return "0 = no maximum. Never below the minimum seats.";
  if (/^PLAN_(PRO|BUSINESS)_MIN_CHARGE_/.test(k)) return "Until saved, the platform minimum (Customer rates) applies.";
  if (k === "note_max_duration_seconds") return "Seconds, at least 30.";
  return null;
}

/**
 * Pay-as-you-go Meeting Notes without a plan (W19, B23 24-10-2026): its own
 * rate and minimum per recording, the keys Customer rates edits too. Not on
 * the GET /api/admin/plans card settings, so the page makes the two rows per
 * currency from GET /api/admin/settings and the card's `rates`.
 */
export const noteRateKey = (cur: PlanCurrency) => `RATE_NOTE_PER_MINUTE_${cur}_MINOR`;
export const noteMinKey = (cur: PlanCurrency) => `MIN_CHARGE_NOTE_${cur}_MINOR`;
const NOTE_MIN_RE = /^MIN_CHARGE_NOTE_(GBP|USD)_MINOR$/;

/**
 * The Free card's Notes pay-as-you-go settings. `effective` is what billing
 * reads (the card's `rates`, else the stored row); a currency with neither is
 * left out (the row then shows "–", read-only). Keys the card already sends
 * are not doubled.
 */
export function notesPaygSettings(
  card: { settings: PlanSetting[]; rates: Record<string, { noteMinor?: number; noteMinChargeMinor?: number }> | null },
  stored: Record<string, { value?: unknown } | undefined> | undefined,
  rule?: PlanRule | null,
): PlanSetting[] {
  const have = new Set(card.settings.map((s) => s.key));
  const out: PlanSetting[] = [];
  for (const cur of PLAN_CURRENCIES) {
    const r = card.rates?.[cur];
    for (const [key, label, fromRates] of [
      [noteRateKey(cur), "Meeting Notes after the free minutes, per minute", r?.noteMinor],
      [noteMinKey(cur), "Minimum per Notes recording", r?.noteMinChargeMinor],
    ] as const) {
      if (have.has(key)) continue;
      const raw = stored?.[key]?.value;
      const value = raw == null || String(raw).trim() === "" ? null : String(raw).trim();
      const parsed = value != null && /^\d+$/.test(value) ? parseInt(value, 10) : null;
      const effective = typeof fromRates === "number" ? fromRates : parsed;
      if (effective == null) continue;
      out.push({ key, label, kind: "minor", currency: cur, value, effective, rule: rule ?? null });
    }
  }
  return out;
}

/** A per-plan interpretation minimum (B20): money per session. */
export function isSessionMinimumKey(key: string): boolean {
  return /^PLAN_(PRO|BUSINESS)_MIN_CHARGE(_REALTIME)?_(GBP|USD)_MINOR$/.test(key);
}

/** Per-minute rates that may be a fraction of a penny (backend FRACTIONAL_MINOR_RE). */
const FRACTIONAL_MINOR_RE =
  /^(RATE_BOARDROOM_PER_MINUTE|PLAN_[A-Z0-9_]+_BOARDROOM_RATE)_[A-Z]{3}_MINOR$/;

export function isFractionalMinor(key: string): boolean {
  return FRACTIONAL_MINOR_RE.test(key);
}

/** Smallest whole value the backend accepts (planSettings.mjs PLAN_NUMBER_RULES). */
export function minFor(s: Pick<PlanSetting, "key" | "kind">): number {
  if (s.key in MIN_BY_KEY) return MIN_BY_KEY[s.key];
  if (s.kind === "limit") return -1;
  if (s.kind === "days" || s.kind === "seats" || HOURS_KEYS.has(s.key)) return 1;
  return 0;
}

/** Trailing zeros off, but keep two decimals: "0.0120" → "0.012", "14.9" → "14.90". */
function trimMoney(v: string): string {
  const [whole, frac = ""] = v.split(".");
  const kept = frac.replace(/0+$/, "").padEnd(2, "0");
  return `${whole}.${kept}`;
}

/** 1499 → "14.99"; 1.2 → "0.012". */
export function minorToMajor(minor: number): string {
  if (!Number.isFinite(minor)) return "";
  return trimMoney((minor / 100).toFixed(6));
}

/**
 * "14.99" → "1499"; "0.012" → "1.2" when fractions are allowed, otherwise
 * at most two decimals. Blank or not a number → null.
 */
export function majorToMinor(v: string, fractional: boolean): string | null {
  const t = v.trim();
  const re = fractional ? /^\d+(\.\d{0,6})?$/ : /^\d+(\.\d{0,2})?$/;
  if (t === "" || !re.test(t)) return null;
  const minor = Number((Number(t) * 100).toFixed(4));
  return fractional ? String(minor) : String(Math.round(minor));
}

/** What the form shows for a setting (from `effective`). */
export function toDisplay(s: PlanSetting): string {
  const e = s.effective;
  if (s.kind === "switch") return e === true ? "true" : "false";
  if (s.kind === "choice") return typeof e === "string" && e ? e : (s.options?.[0] ?? "");
  if (s.kind === "other") return e == null ? "" : String(e);
  if (typeof e !== "number" || !Number.isFinite(e)) return "";
  if (s.kind === "minor") return minorToMajor(e);
  if (HOURS_KEYS.has(s.key)) return String(Number((e / 60).toFixed(2)));
  return String(e);
}

/** Display value → the string the backend stores; null = cannot be saved. */
export function toStored(s: PlanSetting, display: string): string | null {
  const t = display.trim();
  if (s.kind === "switch") return t === "true" ? "true" : "false";
  if (s.kind === "choice") return s.options?.includes(t) ? t : null;
  if (s.kind === "other") return null;
  if (s.kind === "minor") return majorToMinor(t, isFractionalMinor(s.key));
  if (HOURS_KEYS.has(s.key)) {
    if (!/^\d+(\.\d{0,2})?$/.test(t)) return null;
    const minutes = Math.round(Number(t) * 60);
    return minutes >= minFor(s) ? String(minutes) : null;
  }
  if (!/^-?\d+$/.test(t)) return null;
  const n = parseInt(t, 10);
  return n >= minFor(s) ? String(n) : null;
}

/** The label as the page shows it: fair use in hours, not minutes. */
export function displayLabel(s: PlanSetting): string {
  const plain = PLAIN_LABELS[s.key];
  if (plain) return plain;
  if (/^PLAN_(PRO|BUSINESS)_MIN_CHARGE_REALTIME_/.test(s.key)) return "Minimum per realtime session";
  if (isSessionMinimumKey(s.key)) return "Minimum per interpretation session";
  if (HOURS_KEYS.has(s.key)) return s.label.replace(/,? ?minutes a month$/, ", hours a month");
  return s.label;
}

export function settingsToForm(settings: PlanSetting[]): FormValues {
  return Object.fromEntries(settings.map((s) => [s.key, toDisplay(s)]));
}

export interface PlanChange {
  key: string;
  /** "Pro: price a month (GBP)" */
  label: string;
  from: string;
  to: string;
  /** The stored value Save sends (for the impact count). */
  stored?: string;
  /**
   * B20: shown, not sent. The Free number didn't change but the reset mode
   * did, so what it means did ("600 / month" → "600, once").
   */
  implied?: boolean;
}

export interface ChangedPlanSettings {
  /** Only the keys the admin changed, in stored form. */
  settings: Record<string, string>;
  /** Edited fields that cannot be saved. */
  invalid: string[];
  /** One line per change for "Will change: …". */
  lines: PlanChange[];
}

/**
 * Human value for the "Will change" list: "£14.99", "Unlimited", "20 h",
 * "On". `mode` (B20): the Free reset mode this value belongs to, so the Free
 * numbers say "/ month" or ", once".
 */
export function readable(s: PlanSetting, display: string, mode?: FreeResetMode): string {
  const t = display.trim();
  if (s.kind === "switch") return display === "true" ? "On" : "Off";
  if (s.kind === "choice") return CHOICE_LABELS[t] ?? t;
  if (s.kind === "limit" && t === "-1") {
    return /NOTES_MINUTES$/.test(s.key) ? "Unlimited, fair use" : "Unlimited";
  }
  const zero = zeroMeaning(s.key);
  if (zero && t !== "" && Number(t) === 0) return zero;
  if (s.kind === "minor" && s.currency) return `${CURRENCY_SYMBOL[s.currency]}${display}`;
  if (HOURS_KEYS.has(s.key)) return `${display} h`;
  if (t !== "" && mode && (FREE_MODE_NUMBER_KEYS as readonly string[]).includes(s.key)) {
    const unit = s.key === "FREE_PLAN_NOTES_MINUTES" ? " min" : "";
    return mode === "one_time" ? `${t}${unit}, once` : `${t}${unit} / month`;
  }
  if (s.key === FREE_EXPIRY_KEY && t !== "") return `After ${t} ${t === "1" ? "day" : "days"}`;
  if (s.kind === "days" && t !== "") return `${t} ${t === "1" ? "day" : "days"}`;
  if (s.kind === "seconds" && t !== "") return `${t} s`;
  return display;
}

/**
 * The Save payload: a setting is sent only when its stored value differs
 * from what the page loaded, so untouched fields (every other currency
 * included) are never written.
 */
export function changedPlanSettings(
  entries: { setting: PlanSetting; group: string }[],
  baseline: FormValues,
  current: FormValues,
): ChangedPlanSettings {
  const settings: Record<string, string> = {};
  const invalid: string[] = [];
  const lines: PlanChange[] = [];
  // B20: the Free numbers read by the mode they belong to, before and after.
  const modeBefore = freeModeOf(baseline[FREE_RESET_MODE_KEY]);
  const modeAfter = freeModeOf(current[FREE_RESET_MODE_KEY] ?? baseline[FREE_RESET_MODE_KEY]);
  const modeChanged = modeBefore !== modeAfter && FREE_RESET_MODE_KEY in baseline;
  for (const { setting, group } of entries) {
    const before = baseline[setting.key] ?? "";
    const after = current[setting.key] ?? before;
    const cur = setting.currency ? ` (${setting.currency})` : "";
    const label = `${group}: ${displayLabel(setting)}${cur}`;
    const freeNumber = (FREE_MODE_NUMBER_KEYS as readonly string[]).includes(setting.key);
    const same = after === before || (toStored(setting, after) !== null && toStored(setting, after) === toStored(setting, before));
    if (same) {
      // The number stays but now means something else: say so, send nothing.
      if (modeChanged && freeNumber && before.trim() !== "") {
        lines.push({
          key: setting.key,
          label,
          from: readable(setting, before, modeBefore),
          to: readable(setting, before, modeAfter),
          implied: true,
        });
      }
      continue;
    }
    const next = toStored(setting, after);
    if (next === null) {
      invalid.push(setting.key);
      continue;
    }
    settings[setting.key] = next;
    lines.push({
      key: setting.key,
      label,
      from: readable(setting, before, freeNumber ? modeBefore : undefined),
      to: readable(setting, after, freeNumber ? modeAfter : undefined),
      stored: next,
    });
  }
  return { settings, invalid, lines };
}

// ---------------------------------------------------------------------------
// Card layout (W8, DES 22-10-2026): one value per row, label left, value
// right with its unit ("600 min / month", "Unlimited", "60p / min"). These
// only format what the form holds; the stored values and Save are unchanged.
// ---------------------------------------------------------------------------

/** Where a setting sits inside a plan card. */
export type PlanRowGroup = "switch" | "mode" | "price" | "included" | "payg";

export function planRowGroup(s: PlanSetting): PlanRowGroup {
  if (s.kind === "switch") return "switch";
  // B20: the Free reset mode (+ its expiry) sit with the switches, as controls.
  if (s.kind === "choice" || s.key === FREE_EXPIRY_KEY) return "mode";
  if (s.kind === "minor") return s.interval ? "price" : "payg";
  return "included";
}

export const CURRENCY_REGION: Record<PlanCurrency, string> = {
  GBP: "United Kingdom",
  USD: "Everyone else",
};

/** Row label: the unit after the value carries "a month" / "per minute". */
export function rowLabel(s: PlanSetting): string {
  if (planRowGroup(s) === "price" && s.currency) return CURRENCY_REGION[s.currency];
  const t = displayLabel(s)
    .replace(/^No plan: /, "")
    .replace(/,? (minutes|hours) a month$/, "")
    .replace(/ per seat a month/, "")
    .replace(/ a (month|year)( per seat)?$/, "")
    .replace(/ per minute$/, "")
    // The value says "min": "Intelligence minutes" → "Intelligence".
    .replace(/ minutes\b/, s.kind === "minutes" ? "" : " minutes");
  return t.charAt(0).toUpperCase() + t.slice(1);
}

const whole = (n: number) => n.toLocaleString("en-GB");

/** "14.99" + GBP → "£14.99"; under a pound/dollar per minute → "40p", "1.2p", "50¢". */
function moneyText(display: string, currency: PlanCurrency, small: boolean): string {
  const minor = majorToMinor(display, true);
  if (small && minor !== null && Number(minor) < 100) {
    return `${minor}${currency === "GBP" ? "p" : "¢"}`;
  }
  return `${CURRENCY_SYMBOL[currency]}${display}`;
}

/** A read-only money row: "40p / min", "£1.00 / meeting", "$2.00 / session". */
export function minorRowText(
  minor: number,
  currency: PlanCurrency,
  unit: "min" | "meeting" | "session" | "recording",
): string {
  return `${moneyText(minorToMajor(minor), currency, unit === "min")} / ${unit}`;
}

/** What a row shows on the right, unit after the value. */
export function rowValue(s: PlanSetting, display: string): string {
  const t = display.trim();
  if (s.kind === "switch") return t === "true" ? "On" : "Off";
  if (s.kind === "choice") return CHOICE_LABELS[t] ?? (t || "Not set");
  if (t === "") return "Not set";
  if (s.kind === "minor" && s.currency) {
    if (s.interval) return `${CURRENCY_SYMBOL[s.currency]}${t} / ${s.interval}`;
    if (isSessionMinimumKey(s.key)) return `${moneyText(t, s.currency, false)} / session`;
    if (NOTE_MIN_RE.test(s.key)) return `${moneyText(t, s.currency, false)} / recording`;
    if (/^MIN_CHARGE_/.test(s.key)) return `${moneyText(t, s.currency, false)} / meeting`;
    return `${moneyText(t, s.currency, true)} / min`;
  }
  const n = Number(t);
  if (!Number.isFinite(n)) return t;
  const zero = zeroMeaning(s.key);
  if (zero && n === 0) return zero;
  if (s.key === FREE_EXPIRY_KEY) return `After ${whole(n)} ${n === 1 ? "day" : "days"}`;
  if (s.kind === "seconds") {
    const min = n / 60;
    const tail = Number.isInteger(min) ? ` (${whole(min)} min)` : "";
    return `${whole(n)} s${tail}`;
  }
  if (HOURS_KEYS.has(s.key)) {
    return `${whole(n)} ${n === 1 ? "hour" : "hours"} / ${s.key.includes("BUSINESS") ? "person / " : ""}month`;
  }
  if (/^PLAN_(PRO|BUSINESS)_NOTES_MINUTES$/.test(s.key)) {
    if (n === -1) return "Unlimited, fair use";
    return `${whole(n)} min / ${s.key.includes("BUSINESS") ? "person / " : ""}month`;
  }
  if (s.kind === "limit" && n === -1) return "Unlimited";
  if (s.kind === "minutes") {
    if (s.key === "FREE_INTERPRET_MINUTES") return `${whole(n)} min, once`;
    if (s.key === "PLAN_BUSINESS_SOFT_CAP_MINUTES") return `${whole(n)} min / seat / month`;
    return `${whole(n)} min / month`;
  }
  if (s.kind === "count" || s.kind === "limit") return `${whole(n)} / month`;
  if (s.kind === "days") return `${whole(n)} ${n === 1 ? "day" : "days"}`;
  if (s.kind === "seats") return `${whole(n)} ${n === 1 ? "seat" : "seats"}`;
  return t;
}

/** The unit the in-place editor shows around the number: ["£", "/ month"]. */
export function editAffixes(s: PlanSetting): [string, string] {
  if (s.kind === "minor" && s.currency) {
    const sym = CURRENCY_SYMBOL[s.currency];
    if (s.interval) return [sym, `/ ${s.interval}`];
    if (isSessionMinimumKey(s.key)) return [sym, "/ session"];
    if (NOTE_MIN_RE.test(s.key)) return [sym, "/ recording"];
    return [sym, /^MIN_CHARGE_/.test(s.key) ? "/ meeting" : "/ min"];
  }
  if (s.kind === "seconds") return ["", "seconds"];
  if (HOURS_KEYS.has(s.key)) return ["", "hours"];
  if (s.kind === "minutes") return ["", "min"];
  if (s.kind === "days") return ["", "days"];
  if (s.kind === "seats") return ["", "seats"];
  return ["", "/ month"];
}
